/**
 * The course engine's pure core — no React, no worker.
 *
 * `StockfishEngine` reports one `onResult(...)` call per UCI `info … pv` line.
 * `foldEngineResult` folds those into a single display state:
 *
 *   {fen, depth, lines: [{rank, depth, san, uci, score, display}], best}
 *
 * Scores leave this module in WHITE'S point of view (UCI reports them for the
 * side to move), and a line computed for a position other than the current one
 * is dropped — the window between a move landing and the search restarting is
 * exactly when a stale line would otherwise be drawn on the new position.
 */
import {Chess} from 'chess.js';

/** White-POV score: centipawns, or signed mate distance (+ = White mates). */
export type EngineScore = {cp?: number; mate?: number};

export type EngineLine = {
  /** 1-based MultiPV rank. */
  rank: number;
  depth: number;
  san: string[];
  uci: string[];
  score: EngineScore;
  /** "+1.2" | "-0.4" | "0.0" | "#3" | "#-2" */
  display: string;
};

export type EngineState = {
  fen: string;
  /** Depth of line 1 — what the panel reports. 0 before the first line. */
  depth: number;
  /** Sorted by rank. */
  lines: EngineLine[];
  /** The best line's first move, for an arrow. */
  best: {from: string; to: string} | null;
};

/** One `onResult` call, as named arguments. */
export type EngineResult = {
  variationNumber: number;
  movesSAN: string[];
  baseTurn: 'w' | 'b';
  score: string;
  isMate: boolean;
  raw: string;
  searchedFen?: string;
};

/** Where the engine is, as the panel reports it. */
export type EngineStatus =
  | 'off'
  | 'starting' // on, nothing heard from the worker yet
  | 'thinking' // searching the current position
  | 'done' // search finished for the current position
  | 'unavailable' // no Web Workers, or the worker never answered
  | 'checkmate'
  | 'stalemate'
  | 'draw';

export function emptyEngineState(fen: string): EngineState {
  return {fen, depth: 0, lines: [], best: null};
}

/** The UCI principal variation from a raw `info … pv …` line. */
export function parseUciPv(raw: string): string[] {
  const at = (raw || '').indexOf(' pv ');
  if (at === -1) return [];
  const tail = raw.slice(at + 4).trim();
  return tail ? tail.split(/\s+/) : [];
}

/** `depth N` from a raw info line (not `seldepth`); 0 when absent. */
export function parseDepth(raw: string): number {
  const m = (raw || '').match(/(?:^|\s)depth (\d+)/);
  return m ? parseInt(m[1], 10) : 0;
}

/** Side-to-move score -> White's point of view. */
export function whitePovScore(score: string, isMate: boolean, baseTurn: 'w' | 'b'): EngineScore {
  const n = Number(score);
  const value = Number.isFinite(n) ? n : 0;
  // `|| 0` folds -0 back to 0, so a level black-to-move line never reads "-0".
  const pov = (baseTurn === 'b' ? -value : value) || 0;
  return isMate ? {mate: pov} : {cp: pov};
}

export function formatEval(score: EngineScore | null | undefined): string {
  if (!score) return '';
  if (score.mate != null) return `#${score.mate}`;
  const pawns = (score.cp ?? 0) / 100;
  const text = Math.abs(pawns).toFixed(1);
  if (text === '0.0') return '0.0';
  return `${pawns > 0 ? '+' : '-'}${text}`;
}

/** Within this many centipawns a position reads as level. */
const EQUAL_CP = 30;
/** From here an edge is called winning rather than better. */
const WINNING_CP = 300;

export function evalSide(score: EngineScore | null | undefined): 'white' | 'black' | 'equal' | null {
  if (!score) return null;
  if (score.mate != null) return score.mate >= 0 ? 'white' : 'black';
  const cp = score.cp ?? 0;
  if (Math.abs(cp) <= EQUAL_CP) return 'equal';
  return cp > 0 ? 'white' : 'black';
}

/** The verdict in words, so the eval never rests on a sign or a colour. */
export function evalWord(score: EngineScore | null | undefined): string {
  const side = evalSide(score);
  if (!score || !side) return '';
  if (side === 'equal') return 'Equal';
  const who = side === 'white' ? 'White' : 'Black';
  if (score.mate != null) return `${who} mates in ${Math.abs(score.mate)}`;
  return Math.abs(score.cp ?? 0) >= WINNING_CP ? `${who} is winning` : `${who} is better`;
}

/**
 * White's share of an eval bar, 0–100. Lichess's winning-chances curve, so a
 * pawn matters near equality and barely at all at +9.
 */
export function whiteShare(score: EngineScore | null | undefined): number {
  if (!score) return 50;
  if (score.mate != null) return score.mate >= 0 ? 100 : 0;
  const cp = Math.max(-1000, Math.min(1000, score.cp ?? 0));
  const chances = 2 / (1 + Math.exp(-0.00368208 * cp)) - 1;
  return 50 + 50 * chances;
}

/**
 * Fold one engine result into the state for `currentFen`.
 *
 * - A result searched for another position is ignored (returns `state`).
 * - State still holding an older position is replaced, not merged.
 * - Ranks above `maxLines`, and lines without moves, are dropped.
 */
export function foldEngineResult(
  state: EngineState,
  result: EngineResult,
  currentFen: string,
  maxLines = 3,
): EngineState {
  if (result.searchedFen && result.searchedFen !== currentFen) return state;
  const base = state.fen === currentFen ? state : emptyEngineState(currentFen);

  const rank = result.variationNumber;
  const uci = parseUciPv(result.raw);
  const san = result.movesSAN ?? [];
  if (!(rank >= 1 && rank <= maxLines) || !uci.length || !san.length) return base;

  const depth = parseDepth(result.raw);
  const score = whitePovScore(result.score, result.isMate, result.baseTurn);
  const line: EngineLine = {rank, depth, san, uci, score, display: formatEval(score)};
  const lines = base.lines.filter(l => l.rank !== rank).concat(line).sort((a, b) => a.rank - b.rank);

  if (rank !== 1) return {...base, lines};
  const first = uci[0];
  return {
    fen: base.fen,
    depth,
    lines,
    best: first.length >= 4 ? {from: first.slice(0, 2), to: first.slice(2, 4)} : null,
  };
}

/** The best move as `[from, to]`, the shape chessboard-package's `customArrows` takes. */
export function bestArrow(state: EngineState): [string, string] | null {
  return state.best ? [state.best.from, state.best.to] : null;
}

/** "27. Bb5 a6 28. Ba4 …" — numbered from the position's own move counter. */
export function formatSanLine(fen: string, san: string[], maxPlies = 8): string {
  if (!san.length) return '';
  const parts = (fen || '').trim().split(/\s+/);
  let black = parts[1] === 'b';
  let num = parseInt(parts[5], 10);
  if (!Number.isFinite(num) || num < 1) num = 1;

  const out: string[] = [];
  san.slice(0, maxPlies).forEach((move, i) => {
    if (!black) out.push(`${num}. ${move}`);
    else if (i === 0) out.push(`${num}… ${move}`);
    else out.push(move);
    if (black) num += 1;
    black = !black;
  });
  return out.join(' ') + (san.length > maxPlies ? ' …' : '');
}

/** Why there is nothing to analyse, or null for a live position. */
export function gameOverReason(fen: string): 'checkmate' | 'stalemate' | 'draw' | null {
  let chess: Chess;
  try {
    chess = new Chess(fen);
  } catch {
    return null;
  }
  if (chess.isCheckmate()) return 'checkmate';
  if (chess.isStalemate()) return 'stalemate';
  if (chess.isDraw()) return 'draw';
  return null;
}

export type Agreement =
  | {kind: 'pending'}
  | {kind: 'agrees'; san: string}
  /** The solution is not the engine's first choice but scores as well. */
  | {kind: 'equal'; san: string; engineSan: string}
  | {kind: 'differs'; engineSan: string; engineDisplay: string};

const bareSan = (san: string) => (san || '').replace(/[+#!?]/g, '').trim();

function sameMove(line: EngineLine, expected: {san?: string; uci?: string}): boolean {
  const uci = line.uci[0]?.toLowerCase();
  if (expected.uci && uci) return expected.uci.toLowerCase() === uci;
  return !!expected.san && bareSan(expected.san) === bareSan(line.san[0]);
}

function sameScore(a: EngineScore, b: EngineScore): boolean {
  if (a.mate != null || b.mate != null) return a.mate === b.mate;
  return Math.abs((a.cp ?? 0) - (b.cp ?? 0)) <= EQUAL_CP;
}

/**
 * Does the engine's choice match the expected move (a puzzle's solution)?
 * Null with nothing to compare; pending until line 1 reaches `minDepth`,
 * because a depth-4 opinion is noise. An alternative the engine scores as well
 * as its own best is agreement — mate puzzles often have several mates.
 */
export function compareWithEngine(
  state: EngineState,
  expected: {san?: string; uci?: string} | null | undefined,
  minDepth = 12,
): Agreement | null {
  if (!expected || (!expected.san && !expected.uci)) return null;
  const best = state.lines[0];
  if (!best || best.rank !== 1 || state.depth < minDepth) return {kind: 'pending'};
  if (sameMove(best, expected)) return {kind: 'agrees', san: best.san[0]};
  const alt = state.lines.find(l => l.rank > 1 && sameMove(l, expected));
  if (alt && sameScore(best.score, alt.score)) return {kind: 'equal', san: alt.san[0], engineSan: best.san[0]};
  return {kind: 'differs', engineSan: best.san[0], engineDisplay: best.display};
}

/**
 * Colour for the best-move arrow: a deep blue that reads on both square
 * colours and is not mistaken for the package's own yellow drawn arrows or
 * for a pass/fail status colour.
 */
export const ENGINE_ARROW_COLOR = 'rgba(0,48,136,0.8)';
